import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CsrAPI } from "../apis/CsrAPI";
import { useSession } from "./SessionContext";
import {
  dashboardPermission,
  PERMISSIONS_NOT_FOUND_PATH,
} from "../utils/permissions";

const PermissionsContext = createContext(null);

export const PermissionsProvider = () => {
  const [permissions, setPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  // A 404 means the RBAC endpoint itself is missing, which is not a
  // permission answer we can fail closed on: the whole app goes to NotFound.
  const [notFound, setNotFound] = useState(false);
  const { isAuthenticated, isLoading } = useAuth0();
  const { sessionReady } = useSession();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setPermissions({});
      setErrMsg("");
      setNotFound(false);
      setPermissionsLoading(false);
      return;
    }

    // /me/permissions needs the session the login call establishes, so it waits
    // for sessionReady the same way the feature flags do.
    if (!sessionReady) {
      return;
    }

    let cancelled = false;
    setPermissionsLoading(true);
    CsrAPI.getPermissions()
      .then(data => {
        if (cancelled) return;
        setPermissions(data || {});
        setErrMsg("");
        setNotFound(false);
      })
      .catch(error => {
        if (cancelled) return;
        // Failing closed: with no permission map every gated control stays
        // disabled rather than letting through an action the API would reject.
        setPermissions({});
        if (error?.response?.status === 404) {
          // No error message to show: the redirect below replaces the page.
          setErrMsg("");
          setNotFound(true);
          return;
        }
        setErrMsg("Failed to get user permissions");
        setNotFound(false);
      })
      .finally(() => {
        if (!cancelled) {
          setPermissionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, sessionReady]);

  // Only an explicit true grants access; a missing or false key denies it.
  const hasPermission = useCallback(
    key => permissions?.[key] === true,
    [permissions]
  );

  const canOnDashboard = useCallback(
    (studyType, action) =>
      hasPermission(dashboardPermission(studyType, action)),
    [hasPermission]
  );

  if (notFound) {
    return <Navigate to={PERMISSIONS_NOT_FOUND_PATH} replace />;
  }

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        permissionsLoading,
        errMsg,
        hasPermission,
        canOnDashboard,
      }}>
      <Outlet />
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error("Please use PermissionsProvider in parent component");
  }

  return context;
};

export default PermissionsProvider;
